import type { ExternalInvitationManualValues } from '@/types/ExternalInvitationStep.types';
import { MIN_LEN } from '@/constants';

interface ExternalInvitationEmailValidationOptions {
  isDuplicateEmail?: boolean;
}

export const EMPTY_MANUAL_VALUES: ExternalInvitationManualValues = {
  name: '',
  email: '',
  phoneNumber: '',
  organization: '',
};

const RANDOM_PREFIX_LENGTH = 2; // Skip "0." prefix from Math.random() string

export function createRowIdentifier(): string {
  const generatedIdentifier = globalThis.crypto.randomUUID();
  if (generatedIdentifier.length > MIN_LEN) return generatedIdentifier;
  return `${String(Date.now())}-${String(Math.random()).slice(RANDOM_PREFIX_LENGTH)}`;
}

export function isValidEmailAddress(emailValue: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue.trim());
}

function hasMeaningfulPhoneNumber(phoneNumberValue: string): boolean {
  const normalizedPhoneNumber = phoneNumberValue.trim();
  if (normalizedPhoneNumber.length === MIN_LEN) return false;
  if (/^\+\d{1,3}$/.test(normalizedPhoneNumber)) return false;
  return true;
}

export function resolveEmailErrorMessage(
  values: ExternalInvitationManualValues,
  options: ExternalInvitationEmailValidationOptions = {},
): string | undefined {
  if (options.isDuplicateEmail === true) {
    return 'This email already exists. Add the user from Contacts instead.';
  }
  if (values.email.trim().length === MIN_LEN) return undefined;
  if (isValidEmailAddress(values.email)) return undefined;
  return 'Please enter a valid email address';
}

export function resolveIsAddDisabled(
  values: ExternalInvitationManualValues,
  options: ExternalInvitationEmailValidationOptions = {},
): boolean {
  if (values.name.trim().length === MIN_LEN) return true;
  if (values.email.trim().length === MIN_LEN) return true;
  if (!hasMeaningfulPhoneNumber(values.phoneNumber)) return true;
  if (values.organization.trim().length === MIN_LEN) return true;
  if (options.isDuplicateEmail === true) return true;
  return !isValidEmailAddress(values.email);
}

export function resolveHasPendingManualEntries(valuesList: ExternalInvitationManualValues[]): boolean {
  return valuesList.some((values) =>
    values.name.trim().length > MIN_LEN ||
    values.email.trim().length > MIN_LEN ||
    hasMeaningfulPhoneNumber(values.phoneNumber) ||
    values.organization.trim().length > MIN_LEN,
  );
}

export function createInitialManualRows(): Array<{
  id: string;
  values: ExternalInvitationManualValues;
}> {
  return Array.from({ length: 3 }, () => ({
    id: createRowIdentifier(),
    values: EMPTY_MANUAL_VALUES,
  }));
}
